// Email Processing Service - Step 4: N8N Webhook Integration
// Handles secure email processing through backend API

import backendApiService from './backendApi.js';

// Configuration
const N8N_WEBHOOK_URL_PROD = import.meta.env.VITE_N8N_WEBHOOK_URL_PROD || 'https://prismdigitallabs.app.n8n.cloud/webhook/extract-camp-emails';
const N8N_WEBHOOK_URL_TEST = import.meta.env.VITE_N8N_WEBHOOK_URL_TEST || 'https://prismdigitallabs.app.n8n.cloud/webhook-test/extract-camp-emails';
const REQUEST_TIMEOUT = 30000; // 30 seconds

// Helper function to get the appropriate webhook URL
const getWebhookUrl = (useTest = false) => {
  return useTest ? N8N_WEBHOOK_URL_TEST : N8N_WEBHOOK_URL_PROD;
};
const RETRY_CONFIG = {
  maxAttempts: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 9000, // 9 seconds
  backoffMultiplier: 3
};

// Utility function to create custom errors
const createProcessingError = (message, code, statusCode, retryable = false, batchId = null) => {
  const error = new Error(message);
  error.code = code;
  error.statusCode = statusCode;
  error.retryable = retryable;
  error.batchId = batchId;
  return error;
};

// Utility function to calculate retry delay with exponential backoff
const calculateRetryDelay = (attempt, config = RETRY_CONFIG) => {
  const delay = Math.min(
    config.baseDelay * Math.pow(config.backoffMultiplier, attempt - 1),
    config.maxDelay
  );
  return delay;
};

// Utility function to sleep for specified milliseconds
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// HTTP client with timeout and retry logic
const makeWebhookRequest = async (payload, attempt = 1) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    console.log(`[EmailProcessing] Attempt ${attempt}: Calling N8N webhook`, {
      user_id: payload.user_id,
      email_address: payload.email_address,
      processing_type: payload.processing_type,
      batch_id: payload.batch_id
    });

    const response = await fetch(getWebhookUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': import.meta.env.VITE_N8N_WEBHOOK_TOKEN,
        'User-Agent': 'CampConnect-EmailProcessor/1.0'
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[EmailProcessing] HTTP ${response.status}:`, errorText);
      
      // Handle different HTTP status codes
      if (response.status === 401 || response.status === 403) {
        throw createProcessingError(
          'Authentication failed - token may be expired',
          'AUTH_ERROR',
          response.status,
          false // Don't retry auth errors
        );
      } else if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const delay = retryAfter ? parseInt(retryAfter) * 1000 : calculateRetryDelay(attempt);
        throw createProcessingError(
          'Rate limited - will retry after delay',
          'RATE_LIMITED',
          response.status,
          true,
          payload.batch_id
        );
      } else if (response.status >= 500) {
        throw createProcessingError(
          'Server error - will retry',
          'SERVER_ERROR',
          response.status,
          true,
          payload.batch_id
        );
      } else {
        throw createProcessingError(
          `HTTP ${response.status}: ${errorText}`,
          'HTTP_ERROR',
          response.status,
          false
        );
      }
    }

    const data = await response.json();
    console.log(`[EmailProcessing] N8N webhook response:`, data);
    return data;

  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw createProcessingError(
        'Request timeout - will retry',
        'TIMEOUT',
        408,
        true,
        payload.batch_id
      );
    }
    
    throw error;
  }
};

// Retry logic with exponential backoff
const retryWebhookRequest = async (payload, config = RETRY_CONFIG) => {
  let lastError;
  
  for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
    try {
      return await makeWebhookRequest(payload, attempt);
    } catch (error) {
      lastError = error;
      
      // Don't retry non-retryable errors
      if (!error.retryable) {
        console.error(`[EmailProcessing] Non-retryable error:`, error.message);
        throw error;
      }
      
      // Don't retry on last attempt
      if (attempt === config.maxAttempts) {
        console.error(`[EmailProcessing] Max retries (${config.maxAttempts}) exceeded`);
        throw error;
      }
      
      // Calculate delay and wait
      const delay = calculateRetryDelay(attempt, config);
      console.log(`[EmailProcessing] Retrying in ${delay}ms (attempt ${attempt + 1}/${config.maxAttempts})`);
      await sleep(delay);
    }
  }
  
  throw lastError;
};

// Main processing functions
export const processHistoricalEmails = async (supabase, userId) => {
  console.log(`[EmailProcessing] Starting historical email processing for user ${userId}`);
  
  try {
    // 1. Get email connection using Step 3 functions
    const connection = await getEmailConnection(userId);
    if (!connection) {
      throw createProcessingError(
        'No email connection found. Please connect your Gmail account first.',
        'NO_CONNECTION',
        404,
        false
      );
    }

    // 2. Decrypt access token using Step 3 functions
    let accessToken;
    try {
      accessToken = decryptToken(connection.access_token_encrypted);
    } catch (error) {
      throw createProcessingError(
        'Failed to decrypt access token',
        'DECRYPT_ERROR',
        500,
        false
      );
    }

    // 3. Prepare payload for N8N webhook with ALL required fields
    const payload = {
      user_id: userId, // Must be a valid UUID from authenticated user
      email_address: connection.email_address, // User's email address
      access_token: accessToken, // Gmail OAuth token
      processing_type: 'historical' // Used for workflow logic
    };

    // Debug: Log ALL fields before sending
    console.log('Sending to N8N:', {
      user_id: payload.user_id,
      email_address: payload.email_address,
      access_token: payload.access_token ? 'present' : 'missing',
      processing_type: payload.processing_type
    });

    // 4. Call N8N webhook with retry logic
    const response = await retryWebhookRequest(payload);

    // 5. Validate response
    if (!response.success) {
      throw createProcessingError(
        response.message || 'N8N processing failed',
        'N8N_ERROR',
        500,
        false,
        response.batch_id
      );
    }

    // 6. Return success result
    const result = {
      success: true,
      batch_id: response.batch_id,
      emails_processed: response.emails_processed || 0,
      emails_extracted: response.emails_extracted || 0,
      message: response.message || 'Historical email processing completed successfully',
      processing_type: 'historical',
      started_at: new Date().toISOString()
    };

    console.log(`[EmailProcessing] Historical processing completed:`, result);
    return result;

  } catch (error) {
    console.error(`[EmailProcessing] Historical processing failed:`, error);
    
    return {
      success: false,
      message: error.message || 'Historical email processing failed',
      error: error.code || 'UNKNOWN_ERROR',
      processing_type: 'historical',
      started_at: new Date().toISOString()
    };
  }
};

export const processDailyEmails = async (supabase, userId) => {
  console.log(`[EmailProcessing] Starting daily email processing for user ${userId}`);
  
  try {
    // 1. Get email connection using Step 3 functions
    const connection = await getEmailConnection(userId);
    if (!connection) {
      throw createProcessingError(
        'No email connection found. Please connect your Gmail account first.',
        'NO_CONNECTION',
        404,
        false
      );
    }

    // 2. Decrypt access token using Step 3 functions
    let accessToken;
    try {
      accessToken = decryptToken(connection.access_token_encrypted);
    } catch (error) {
      throw createProcessingError(
        'Failed to decrypt access token',
        'DECRYPT_ERROR',
        500,
        false
      );
    }

    // 3. Prepare payload for N8N webhook with ALL required fields
    const payload = {
      user_id: userId, // Must be a valid UUID from authenticated user
      email_address: connection.email_address, // User's email address
      access_token: accessToken, // Gmail OAuth token
      processing_type: 'daily_sync' // Used for workflow logic
    };

    // Debug: Log ALL fields before sending
    console.log('Sending to N8N:', {
      user_id: payload.user_id,
      email_address: payload.email_address,
      access_token: payload.access_token ? 'present' : 'missing',
      processing_type: payload.processing_type
    });

    // 4. Call N8N webhook with retry logic
    const response = await retryWebhookRequest(payload);

    // 5. Validate response
    if (!response.success) {
      throw createProcessingError(
        response.message || 'N8N processing failed',
        'N8N_ERROR',
        500,
        false,
        response.batch_id
      );
    }

    // 6. Return success result
    const result = {
      success: true,
      batch_id: response.batch_id,
      emails_processed: response.emails_processed || 0,
      emails_extracted: response.emails_extracted || 0,
      message: response.message || 'Daily email processing completed successfully',
      processing_type: 'daily',
      started_at: new Date().toISOString()
    };

    console.log(`[EmailProcessing] Daily processing completed:`, result);
    return result;

  } catch (error) {
    console.error(`[EmailProcessing] Daily processing failed:`, error);
    
    return {
      success: false,
      message: error.message || 'Daily email processing failed',
      error: error.code || 'UNKNOWN_ERROR',
      processing_type: 'daily',
      started_at: new Date().toISOString()
    };
  }
};

// Manual retry function
export const retryEmailProcessing = async (supabase, userId, processingType) => {
  console.log(`[EmailProcessing] Retrying ${processingType} processing for user ${userId}`);
  
  if (processingType === 'historical') {
    return await processHistoricalEmails(supabase, userId);
  } else if (processingType === 'daily') {
    return await processDailyEmails(supabase, userId);
  } else {
    throw createProcessingError(
      'Invalid processing type. Must be "historical" or "daily"',
      'INVALID_TYPE',
      400,
      false
    );
  }
};

// Status checking (placeholder - would need N8N to provide status endpoint)
export const getProcessingStatus = async (batchId) => {
  console.log(`[EmailProcessing] Getting status for batch ${batchId}`);
  
  // This would typically call a status endpoint from N8N
  // For now, return a placeholder response
  return {
    batch_id: batchId,
    status: 'completed', // Placeholder
    emails_processed: 0,
    emails_extracted: 0,
    started_at: new Date().toISOString(),
    processing_type: 'historical'
  };
};

// Webhook health check
export const checkN8NWebhookHealth = async () => {
  console.log(`[EmailProcessing] Checking N8N webhook health`);
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout for health check

    const response = await fetch(getWebhookUrl(), {
      method: 'HEAD', // Use HEAD request for health check
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    
    const isHealthy = response.ok || response.status === 405; // 405 Method Not Allowed is OK for HEAD requests
    console.log(`[EmailProcessing] N8N webhook health: ${isHealthy ? 'HEALTHY' : 'UNHEALTHY'}`);
    
    return isHealthy;

  } catch (error) {
    console.error(`[EmailProcessing] N8N webhook health check failed:`, error);
    return false;
  }
};

// Token refresh helper (integrates with Step 3)
export const refreshTokenIfNeeded = async (supabase, userId) => {
  console.log(`[EmailProcessing] Checking if token refresh is needed for user ${userId}`);
  
  try {
    const connection = await getEmailConnection(userId);
    if (!connection) {
      throw createProcessingError('No email connection found', 'NO_CONNECTION', 404, false);
    }

    // Try to refresh the token
    const refreshResult = await refreshGmailToken(connection.refresh_token_encrypted);
    
    if (refreshResult.success) {
      // Update the connection with new tokens
      await updateEmailConnection(userId, {
        access_token_encrypted: refreshResult.access_token_encrypted,
        refresh_token_encrypted: refreshResult.refresh_token_encrypted || connection.refresh_token_encrypted
      });
      
      console.log(`[EmailProcessing] Token refreshed successfully for user ${userId}`);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error(`[EmailProcessing] Token refresh failed:`, error);
    return false;
  }
};

/**
 * Process user's Gmail for camp-related emails (SECURE VERSION)
 * Uses backend API for secure token decryption
 * @param {string} userId - User ID
 * @param {Object} options - Processing options
 * @returns {Promise<Object>} Processing result
 */
export const processUserEmailsSecure = async (userId, options = {}) => {
  const {
    processing_type = 'historical',
    use_test_webhook = false,
    retry_on_failure = true
  } = options;

  console.log(`[EmailProcessing] Starting secure email processing for user: ${userId}`);
  console.log(`[EmailProcessing] Options:`, { processing_type, use_test_webhook, retry_on_failure });

  try {
    // Use backend API for secure processing
    const result = await backendApiService.triggerEmailScan(userId, {
      processing_type,
      use_test_webhook
    });

    console.log(`[EmailProcessing] Secure email processing completed for user: ${userId}`);
    
    return {
      success: true,
      message: 'Email processing completed successfully',
      data: result.data,
      n8n_result: result.n8n_result
    };

  } catch (error) {
    console.error(`[EmailProcessing] Secure email processing failed for user ${userId}:`, error);
    
    // Handle specific error types
    if (error.message.includes('CONNECTION_NOT_FOUND')) {
      throw createProcessingError(
        'No email connection found for this user',
        'NO_CONNECTION',
        404,
        false
      );
    } else if (error.message.includes('SYNC_DISABLED')) {
      throw createProcessingError(
        'Email sync is disabled for this user',
        'SYNC_DISABLED',
        400,
        false
      );
    } else if (error.message.includes('DECRYPT_ERROR')) {
      throw createProcessingError(
        'Failed to decrypt user tokens',
        'DECRYPT_ERROR',
        500,
        false
      );
    } else if (error.message.includes('N8N_WORKFLOW_ERROR')) {
      throw createProcessingError(
        'N8N workflow failed to process emails',
        'N8N_ERROR',
        500,
        retry_on_failure
      );
    } else {
      throw createProcessingError(
        `Email processing failed: ${error.message}`,
        'PROCESSING_ERROR',
        500,
        retry_on_failure
      );
    }
  }
};

/**
 * Get backend API status
 * @returns {Promise<Object>} Backend status
 */
export const getBackendStatus = async () => {
  try {
    return await backendApiService.getStatus();
  } catch (error) {
    console.error('[EmailProcessing] Failed to get backend status:', error);
    throw error;
  }
};

/**
 * Test backend connectivity
 * @returns {Promise<boolean>} True if backend is reachable
 */
export const testBackendConnectivity = async () => {
  try {
    return await backendApiService.testConnectivity();
  } catch (error) {
    console.error('[EmailProcessing] Backend connectivity test failed:', error);
    return false;
  }
};

// Export configuration for external use
export const getRetryConfig = () => ({ ...RETRY_CONFIG });
export const getWebhookUrlForExport = (useTest = false) => getWebhookUrl(useTest);
export const getRequestTimeout = () => REQUEST_TIMEOUT;
